import type React from "react";

import { PlainTooltip, PixelPlaceholderLoader } from "@similarweb/ui-components";

import type { ISelectedSerp } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/shared/KeywordsReportTypes";
import type { ISerpFeaturesMap } from "app/common/serp/SerpFilterMaps";
import { chosenSites } from "app/common/services/chosenSites";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    buildGroupedSerpFeatures,
    buildSerpFeaturesIcons,
} from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellUtils";
import { SERP_MAP } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTypes";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { SerpIconsComponentWithText } from "./SerpStyledComponents";
import { SerpTableCellTooltipSingle } from "./SerpTableCellTooltipSingle";

interface ISerpTableCellRow {
    serpFailed?: boolean;
    serpLoading?: boolean;
    SearchTerm?: string;
    SerpFeatures?: string[];
    SerpScrapeDate?: string;
    SiteSerpFeatures?: { [key: string]: string[] };
}

export interface ISerpTableCellSingleProps {
    row: ISerpTableCellRow;
    tableOptions?: {
        onSerpIconClick?: (serp: ISelectedSerp) => void;
        sitesForSerpColumn?: Array<{ name: string; color?: string }>;
        presentLastUpdatedSubtitleSerpColumn?: boolean;
        serpFeaturesMap?: ISerpFeaturesMap;
        showSerpFeaturesTitle?: boolean;
        serpFeatureItemBackgroundColor?: string;
    };
}

export const SerpTableCellSingle: React.FC<ISerpTableCellSingleProps> = ({
    row,
    tableOptions: {
        onSerpIconClick,
        sitesForSerpColumn = chosenSites.sitelistForLegend(),
        presentLastUpdatedSubtitleSerpColumn = true,
        serpFeaturesMap = SERP_MAP,
        showSerpFeaturesTitle = true,
        serpFeatureItemBackgroundColor,
    },
}) => {
    if (row.serpFailed) {
        return (
            <PlainTooltip
                placement="top"
                tooltipContent={i18nFilter()("serp.error.tooltip")}
            >
                <div>N/A</div>
            </PlainTooltip>
        );
    }
    if (row.serpLoading) {
        return (
            <PixelPlaceholderLoader
                width={79}
                height={16}
            />
        );
    }
    const { SearchTerm, SerpFeatures = [], SerpScrapeDate, SiteSerpFeatures = {} } = row;
    const serpFeatures = buildGroupedSerpFeatures(
        SerpFeatures,
        SiteSerpFeatures,
        sitesForSerpColumn,
        SerpScrapeDate,
        SearchTerm,
        presentLastUpdatedSubtitleSerpColumn,
        serpFeaturesMap,
    );
    const hasFeaturedSerp = Object.keys(serpFeatures.featured).length > 0;
    const hasNonFeaturedSerp = serpFeatures.nonFeatured.length > 0;
    if (!hasFeaturedSerp && !hasNonFeaturedSerp) {
        return <span>-</span>;
    }
    const { icons, text } = buildSerpFeaturesIcons(
        serpFeatures,
        onSerpIconClick,
        serpFeaturesMap,
        serpFeatureItemBackgroundColor,
    );
    return (
        <SerpTableCellTooltipSingle
            serpFeatures={serpFeatures}
            serpFeaturesMap={serpFeaturesMap}
            showSerpFeaturesTitle={showSerpFeaturesTitle}
        >
            <FlexRow>
                <SerpIconsComponentWithText text={text}>{icons}</SerpIconsComponentWithText>
            </FlexRow>
        </SerpTableCellTooltipSingle>
    );
};
